import { CommonModule } from '@angular/common';
import { Component, ContentChild, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { ListItemDefDirective } from './list-item-def.directive';
import { getRandomId } from '@lib/core';
import { MenuItemContainerDirective } from './menu-item-container.directive';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-list',
  imports: [CommonModule, MenuItemContainerDirective, TranslateModule],
  templateUrl: './list.component.html',
  styleUrl: './list.component.scss'
})
export class ListComponent {
  @Input() items: any[] = [];
  @Input() ariaLabel = '';
  @Input() listId?: string;
  @Input() directClick = false;

  @ViewChild('listContainer', { static: true }) listContainer!: ElementRef;

  @ContentChild(ListItemDefDirective, { read: TemplateRef })
  itemTemplate?: TemplateRef<any>;

  @Output() itemActivated = new EventEmitter<{ item: any; index: number }>();

  currentIndex = -1;
  currentSubIndex = -1;
  uniqueListId: string;

  constructor() {
    this.uniqueListId = this.generateUniqueId();
  }

  ngOnInit() {
    this.uniqueListId = this.listId || this.generateUniqueId();
  }

  private generateUniqueId(): string {
    return `lib-list-${getRandomId()}`;
  }

  inside = false;
  handleMenuFocus(event: FocusEvent) {
    this.currentIndex = -1;
    this.currentSubIndex = -1;
    this.inside = false;
  }

  handleMenuKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case ' ':
      case 'Enter':
        if (this.currentIndex < 0) {
          this.moveToFirst();
          event.preventDefault();
        }
        break;
      case 'Tab':
        if (this.currentIndex >= 0) {
          event.preventDefault();
        }
        break;
    }
  }

  handleKeydown(event: KeyboardEvent, index: number) {
    let preventDefault = false;
    switch (event.key) {
      case 'ArrowDown':
        preventDefault = true;
        if (!this.inside) {
          this.moveToNext(true);
        }
        break;
      case 'ArrowUp':
        preventDefault = true;
        if (!this.inside) {
          this.moveToPrevious(true);
        }
        break;
      case 'Home':
        preventDefault = true;
        this.moveToFirst();
        break;
      case 'End':
        preventDefault = true;
        this.moveToLast();
        break;
      case ' ':
      case 'Enter':
        console.log(this.inside, this.directClick);
        if (!this.directClick) {
          if (!this.inside) {
            this.inside = true;
            this.moveToFirstSubItem();
            preventDefault = true;
          }
        } else {
          this.clickFirstChild();
          preventDefault = true;
        }

        break;
      case 'Tab':
        if (this.inside) {
          preventDefault = event.shiftKey ? this.moveToPreviousSubItem() : this.moveToNextSubItem();
        } else {
          preventDefault = event.shiftKey ? this.moveToPrevious(false) : this.moveToNext(false);
        }
        break;
      default:
        break;
    }

    if (preventDefault) {
      event.preventDefault();
    }
  }

  focusMainMenu() {
    const listElement = this.listContainer.nativeElement as HTMLElement;
    listElement.focus();

    this.unfocusAllMainMenuElements();
  }

  unfocusAllMainMenuElements() {
    const items = this.listContainer.nativeElement.querySelectorAll('[role="menuitem"]');
    items.forEach((item: HTMLElement) => {
      item.setAttribute('tabindex', '-1');
      const subElements = item.querySelectorAll('a, input, button, [role="button"]') as NodeListOf<HTMLElement>;
      subElements.forEach((subEl: HTMLElement) => {
        subEl.setAttribute('tabindex', '-1');
        subEl.classList.remove('focused');
      });
    });
  }

  focusCurentIndex(index) {
    this.currentIndex = index;
    this.resetSubIndex();
    this.updateFocus();
  }

  moveToNext(arrowClick: boolean) {
    if (this.items.length === 0) return false;

    if (arrowClick) {
      this.focusCurentIndex((this.currentIndex + 1) % this.items.length);
      return true;
    }

    if (this.currentIndex + 1 >= this.items.length) {
      this.currentIndex = -1;
      this.resetSubIndex();
      this.focusMainMenu();
      return false;
    } else {
      this.focusCurentIndex(this.currentIndex + 1);
      return true;
    }
  }

  moveToPrevious(arrowClick: boolean) {
    if (this.items.length === 0) return false;

    if (arrowClick) {
      this.focusCurentIndex(this.currentIndex === 0 ? this.items.length - 1 : this.currentIndex - 1);
      return true;
    }

    if (this.currentIndex === 0) {
      this.currentIndex = -1;
      this.resetSubIndex();
      this.focusMainMenu();
      return false;
    } else {
      this.focusCurentIndex(this.currentIndex - 1);
      return true;
    }
  }

  moveToFirst() {
    this.currentIndex = 0;
    this.resetSubIndex();
    this.updateFocus();
  }

  moveToLast() {
    this.currentIndex = this.items.length - 1;
    this.resetSubIndex();
    this.updateFocus();
  }

  private resetSubIndex() {
    this.currentSubIndex = -1;
    this.inside = false;
  }

  moveToFirstSubItem() {
    this.moveToNextSubItem();
  }

  moveToNextSubItem() {
    const interactiveElements = this.getInteractiveElements();
    if (interactiveElements?.length > 0) {
      if (this.currentSubIndex === -1) {
        this.currentSubIndex = 0;
      } else {
        this.currentSubIndex = (this.currentSubIndex + 1) % (interactiveElements.length + 1);
        if (this.currentSubIndex === interactiveElements.length) {
          this.getInteractiveElements(true);
          //this.focusCurentIndex(this.currentSubIndex);
          this.moveToNext(false);
          this.resetSubIndex();

          return false;
        }
      }
      this.updateSubFocus();
    }

    return true;
  }

  moveToPreviousSubItem() {
    const interactiveElements = this.getInteractiveElements();
    if (interactiveElements?.length > 0) {
      if (this.currentSubIndex === 0) {
        this.getInteractiveElements(true);
        //this.focusCurentIndex(this.currentSubIndex);
        this.moveToPrevious(false);
        this.resetSubIndex();

        return false;
      } else {
        this.currentSubIndex -= 1;
      }
      this.updateSubFocus();
    }

    return true;
  }

  clickFirstChild() {
    const el = this.getFirstInteractiveElement();
    el.click();
  }

  updateFocus() {
    const items = this.listContainer.nativeElement.querySelectorAll('[role="menuitem"]');
    items.forEach((item: HTMLElement) => item.setAttribute('tabindex', '-1'));
    const currentItem = items[this.currentIndex] as HTMLElement;
    if (currentItem) {
      currentItem.setAttribute('tabindex', '0');
      currentItem.focus();
    }
  }

  updateSubFocus() {
    const interactiveElements = this.getInteractiveElements(true);
    if (this.currentSubIndex === -1) {
      const currentItem = this.getCurrentListItem();
      if (currentItem) {
        currentItem.setAttribute('tabindex', '0');
        currentItem.focus();
      }
    } else if (interactiveElements[this.currentSubIndex]) {
      const currentSubItem = interactiveElements[this.currentSubIndex];
      this.setSubElementFocus(currentSubItem);
    }
  }

  private clearSubElementsFocus(elements: NodeListOf<HTMLElement>) {
    elements.forEach((el: HTMLElement) => {
      el.setAttribute('tabindex', '-1');
      el.classList.remove('focused');
    });
  }

  private setSubElementFocus(element: HTMLElement) {
    element.setAttribute('tabindex', '0');
    element.focus();
    element.classList.add('focused');
  }

  getItemId(index: number): string {
    return `${this.uniqueListId}-item-${index}`;
  }

  private getInteractiveElements(clearSubElementsFocus = false): HTMLElement[] {
    const currentLi = this.getCurrentListItem();
    if (!currentLi) return [];

    const allInteractive = currentLi.querySelectorAll('a, input, button, [role="button"]') as NodeListOf<HTMLElement>;
    if (clearSubElementsFocus) {
      this.clearSubElementsFocus(allInteractive);
    }

    const elements = Array.from(allInteractive).filter((element) => {
      return !this.isElementDisabled(element);
    });

    return elements;
  }

  private getFirstInteractiveElement() {
    const currentLi = this.getCurrentListItem();
    if (!currentLi) return null;

    const allInteractive = currentLi.querySelectorAll('a, input, button, [role="button"]') as NodeListOf<HTMLElement>;
    return Array.from(allInteractive)?.[0];
  }

  private getCurrentListItem(): HTMLElement | null {
    const items = this.listContainer.nativeElement.querySelectorAll('[role="menuitem"]');
    return (items[this.currentIndex] as HTMLElement) || null;
  }

  private isElementDisabled(element: HTMLElement): boolean {
    if (element.hasAttribute('disabled')) {
      return true;
    }

    if ('disabled' in element && (element as any).disabled === true) {
      return true;
    }

    return false;
  }
}
